// A Zod schema, and a branded TypeScript type, per TypeID prefix.
//
// Adding a kind of id is one line in `ID_PREFIXES` plus one exported schema. The prefixes seeded
// here are the ones the specification itself writes down, plus the two that `Scope` needs; each
// feature adds its own when it lands, exactly as it does with settings.
import { z } from "../zod.js";
import { typeIdPattern } from "./typeid.js";
/**
 * The known prefixes. Keep them short, lower case and unambiguous when read aloud on a support
 * call — which is the whole reason the platform puts TypeIDs on the wire rather than bare UUIDs.
 */
export const ID_PREFIXES = {
    organization: 'org',
    venue: 'ven',
    station: 'stn',
    player: 'ply',
    session: 'ses',
    /** A realtime frame (spec/09 §2). */
    message: 'msg',
    /** A request, echoed in `X-Request-Id` and in every problem document (spec/08 §2.6, §2.11). */
    request: 'req',
    /** A row of the scoping smoke resource (`routes/scope-probe.ts`). Deleted with `meta`. */
    scopeProbe: 'prb',
    /** A row of the audit log (spec/07 §11). */
    auditEntry: 'aud',
    /** An upload in object storage: a venue photo today, avatars and title art later (spec/04 §11). */
    file: 'fil',
    /** A named area on a venue's floor plan (spec/04 §3, F-ORG-03). */
    zone: 'zon',
    /** A price and permission tier of stations: Standard, Premium, Console (spec/04 §3, F-ORG-03). */
    stationClass: 'scl',
    /** A consent row: what a person agreed to, which version, and when (spec/04 §2, F-CMP-06). */
    consent: 'cns',
    /** A person's membership of one organisation — better-auth's `member` row (spec/07 §6.1). */
    member: 'mem',
    /** An invitation to become a member (spec/07 §6.5). */
    invitation: 'inv',
    /** A player's ban at one venue or across one organisation (spec/07 §3.5, F-IAM-07). */
    playerBan: 'ban',
    /** A staff note about a player, kept by one organisation (spec/13 §8, F-ORG-06). */
    playerNote: 'pnt',
    /** A verification of a player: a staff document check today, a digital one later (spec/07 §10, F-CMP-02). */
    idCheck: 'idc',
    /**
     * A signed-in session — better-auth's `session` row, a person's sign-in on one device (spec/07
     * §3.3, F-IAM-12). Not `ses`: a **session** is a player's use of a station (spec/01).
     */
    authSession: 'aus',
    /**
     * A command to a station — a `station_command` row, and the `id` of the `cmd` frame that carries
     * it, which the station's `ack` answers in `re` (spec/09 §5.1, INFRA-18).
     */
    stationCommand: 'cmd',
    /** A paired PC's device identity, the `sub` of its device JWT (spec/07 §5). Named by `station.hello`. */
    device: 'dev',
    /** A pairing in progress: a fresh PC's code, awaiting a staff member's approval (spec/10 §3.2, F-STN-02). */
    devicePairing: 'dpr',
    /**
     * A game in the platform catalogue — HQ's global mirror of an IGDB base game, keyed by our own id
     * with the source `igdb_id` beside it (spec/04 §5, F-CAT-01/-07). The foundation of the `catalog`
     * module's `title`; apps, launchers, launch recipes, detection and the venue library are the
     * deferred follow-on (F-CAT-02 … F-CAT-04).
     */
    game: 'gam',
    /**
     * A trailer video an HQ user uploaded for a catalogue game — one `game_trailer` row (spec/14 §6,
     * F-CAT-01/-07). Trailers are **manual HQ uploads** (the product owner, 2026-09-22): the video is
     * PUT to our object storage, never fetched from IGDB, and served from our media CDN.
     */
    gameTrailer: 'gtr',
    /**
     * One entry in a venue's library — a catalogue `game` a venue offers, on which station classes, in
     * which kiosk category, and whether it is shown to players (spec/04 §5, spec/13 §9.1; F-CAT-02). A
     * venue's own row, drawn from the platform catalogue; the fuller `title` (apps, launchers, launch
     * recipes, detection) and custom apps are the deferred follow-on (F-CAT-03 … F-CAT-06).
     */
    libraryEntry: 'lib',
    /** A billed stretch of a session — one `session_segment` row, one source at one rate (spec/04 §4, F-SES-10). */
    sessionSegment: 'ssg',
    /** One entry in a session's timeline — a staff action, a state change or a milestone (spec/05 §12, F-SES-21). */
    sessionEvent: 'sev',
    /** A ledger account: the running balance of one owner in one unit (spec/06 §2.1, ADR-0008). */
    ledgerAccount: 'lac',
    /** A journal entry: one movement of value, two or more balancing lines (spec/06 §2.2). */
    journalEntry: 'jen',
    /** One line of a journal entry, posting a signed amount to one account (spec/06 §2.2). */
    ledgerLine: 'lln',
    /** A menu category — a heading a venue's products are grouped under (spec/13 §6.1, F-FNB-02). */
    menuCategory: 'mct',
    /** A product: food, drink, retail or a service — anything sold that is not play time (spec/13 §6.1, F-FNB-01). */
    product: 'prd',
    /** A product's variant — a size or option that sets the unit price (spec/13 §6.1). */
    productVariant: 'pvr',
    /** A modifier group on a product — a required or optional set of choices (spec/13 §6.1). */
    productModifierGroup: 'pmg',
    /** One modifier a player may add, with its price (spec/13 §6.1). */
    productModifier: 'pmo',
    /** A food order — a purchase of menu items against a session, station or the counter (spec/06 §8, F-FNB, F-POS-01). */
    foodOrder: 'fod',
    /** One line of a food order: a product, its variant and modifiers, a quantity (spec/06 §8.1). */
    foodOrderLine: 'fol',
    /** One modifier chosen on a food order line, priced at order time (spec/06 §8.1). */
    foodOrderLineModifier: 'flm',
    /** A kitchen ticket (KOT): what one preparation station makes for one order (spec/13 §6.2, F-FNB-04). */
    kitchenTicket: 'ktk',
    /** One item on a kitchen ticket (spec/13 §6.2). */
    kitchenTicketItem: 'kti',
    /** A versioned pass product — what a venue sells as play time on a class (spec/06 §3.1, F-PRC-01). */
    passProduct: 'ppr',
    /** One version of a pass product — the terms a holding was sold under (spec/06 §3.1, F-PRC-01). */
    passProductVersion: 'ppv',
    /** A pass holding — the seconds one player holds, funded through the ledger (spec/06 §3.2, F-PRC-02). */
    passHolding: 'pah',
    /**
     * A guest: a walk-in with no account who plays on a guest pass bought at the counter, identified by its 6-character
     * ticket code (spec/05 §3.4, F-SES-07, ADR-0059). Never a player: it holds the pass until the code is claimed into an
     * account.
     */
    guest: 'gst',
    /** A player's wallet at one organisation — one closed-loop balance (spec/06 §5, ADR-0010, F-WAL-01). */
    wallet: 'wal',
    /** One movement on a wallet — a top-up, a bonus grant or a spend — the statement read model (spec/06 §5, F-WAL-08). */
    walletTransaction: 'wtx',
    /** A point-of-sale sale — the unified mixed-basket order the Sell screen creates (spec/06 §8, F-POS-09/-01). */
    sale: 'sal',
    /** One line of a sale: time, food, retail or other, priced with its GST when placed (spec/06 §8.1, F-POS-01). */
    saleLine: 'sln',
    /** One tender against a sale — a cash or counter-UPI payment, recorded at settlement (spec/06 §9.1, F-POS-12). */
    salePayment: 'spy',
    /** A refund against a sale — partial, to a chosen tender, with an approval path (spec/06 §8.7, F-POS-08). */
    saleRefund: 'srf',
    /** The document a paid sale gets: a bill, bill of supply or tax invoice by its tax mode (spec/06 §10.2, F-POS-13). */
    saleDocument: 'sdc',
    /** A discount code an organisation defines and staff enter at checkout (spec/06 §8.4, F-POS-02). */
    discountCode: 'dsc',
    /** A staff member's till session — opened with a float, closed with a blind count (spec/13 §10, F-OPS-01/-03). */
    shift: 'shf',
    /** A cash movement on a shift's drawer — a pay-in, a pay-out or a drop to the safe (spec/13 §10, F-OPS-02). */
    cashMovement: 'cmv',
    /** A venue's business-day close — the day's takings reconciled across tenders (spec/13 §10, F-OPS-06). */
    businessDayClose: 'bdc',
    /** A counter at a venue — where a cash drawer is kept and a shift starts (ADR-0032, Settings → Counters). */
    counter: 'cnt',
    /** A player's call for help on a station — one `session_call` row through its lifecycle (spec/05 §11, F-SES-25). */
    sessionCall: 'cll',
    /**
     * A reservation of a station class for a window (spec/05 §10, F-RES-01). The `reservations` module
     * that owns the booking is a follow-on; the prefix exists so a session's reservation check-in
     * (F-SES-05) can name the reservation it was started from before the module lands.
     */
    reservation: 'rsv',
    /** One inbox notification — the durable record a person reads in the app (spec/16 §2, F-NTF-03). */
    notification: 'ntf',
    /** One delivery attempt of a notification on one channel, with its provider reference (spec/16 §6, F-NTF-03). */
    messageDelivery: 'mdl',
    /** A person's notification preferences at one organisation — categories against channels, quiet hours (spec/16 §3, F-NTF-01). */
    notificationPreference: 'npf',
    /** An automatic promotion applied to a sale by rule (spec/06 §8.4, F-POS-03). */
    promotion: 'prm',
    /** A batch of coupons that grant a pass, wallet value, points or a voucher (spec/06 §8.5, F-POS-06). */
    couponBatch: 'cpb',
    /** One coupon code within a batch (spec/06 §8.5, F-POS-06). */
    coupon: 'cpn',
    /** One redemption of a coupon by a player (spec/06 §8.5, F-POS-06). */
    couponRedemption: 'cpr',
    /** A player's on-account credit limit at one organisation (spec/06 §8.6, F-POS-07). */
    creditAccount: 'cra',
    /** One dated change of a venue's tax mode and GSTIN (spec/06 §10.4, spec/04 §6, F-PAY-11). */
    venueTaxRegistration: 'vtr',
    /**
     * An online payment intent — what the provider was asked to collect for a sale or a wallet top-up,
     * and what became of it (spec/06 §9.2, §9.3, F-PAY-03). Settled money is recorded as the sale's
     * payment or the wallet's top-up, never on the intent alone.
     */
    paymentIntent: 'pit',
    /**
     * An organisation's own custom app — a title not in the platform catalogue that a venue operator adds by
     * hand (name, icon, launch recipe, detection), usable at every venue of the organisation (spec/04 §5,
     * spec/13 §9; F-CAT-02). Org data, never a row of the platform `game` table.
     */
    customApp: 'app',
    /**
     * A pass bought from the kiosk (F-KIO-14): the player's purchase attempt on a station's session — its
     * sale, its tender (wallet, pay at the desk, UPI QR, on account) and whether the pass is applied yet.
     */
    passPurchase: 'ppu',
    /**
     * A one-time offline staff code a venue issued from the console (spec/05 §8.4a, F-SES-24): N minutes of
     * play on any of the venue's stations while they cannot reach the cloud. Only a salted hash is stored.
     */
    offlineStaffCode: 'osc',
    /**
     * A session a station ran while it could not reach the cloud — started with an offline staff code or by
     * a known player's offline sign-in — as the station reported it on reconnect (spec/05 §8.5, F-SES-23/-24).
     */
    offlineSession: 'ofs',
    /**
     * One title played within a session, from start to stop, detected by the station (spec/01 "Play record",
     * spec/04 §4 `game_play`; F-CAT-04): what "Priya played Valorant for 50 minutes" is built from.
     */
    gamePlay: 'gpl',
    /**
     * A health or security alert a station raised (or the api raised for it), open until the condition clears
     * (spec/04 §3 `station_alert`, spec/10 §6.6; F-STN-11).
     */
    stationAlert: 'sal',
    /**
     * An organisation's tax category — name, HSN or SAC code, GST rate — that products, passes and custom lines
     * are taxed by (spec/06 §10.1, F-PAY-11).
     */
    taxCategory: 'txc',
    /** A credit note — the tax document a refund of a taxed line gets, in its own series (spec/06 §10.2, F-CMP-07). */
    creditNote: 'crn',
    /**
     * A request to HQ to add a title to the platform catalogue — a title a venue's PCs have that no catalogue title
     * matches (spec/13 §9.1 "Detected, not in library"; F-CAT-03, F-CAT-08). HQ's inbox: one row per detected title.
     */
    catalogueRequest: 'ctr',
    /**
     * A wallet top-up a player asked for at the kiosk (F-KIO-10, spec/12 §7.1): the amount and offer, paid at the desk
     * (the counter completes it) or by UPI (the provider's capture credits the wallet). Nothing is credited until paid.
     */
    walletTopUpRequest: 'wtq',
    /**
     * A printer a venue registered — a cloud printer that polls, a desk device's printer, or browser printing
     * (spec/13 §11; F-OPS-08).
     */
    printer: 'prn',
    /** One print job: a kitchen ticket, a receipt, a test page — recorded with its status (spec/04 §9 `print_job`; F-OPS-08). */
    printJob: 'pjb',
    /**
     * One published version of a venue's rate card — the pay-as-you-go price per station class, its time-of-day bands,
     * billing unit, minimum charge and daily cap, from `effectiveFrom` (spec/06 §4; F-PRC-10 … F-PRC-12).
     */
    rateCardVersion: 'rcv',
    /**
     * A staff-run station's countdown-screen link (ADR-0033): the revocable, read-only display token a tablet or TV
     * opens at `/display/{token}`. Only the token's SHA-256 is stored; this id names the link, never the token.
     */
    stationDisplay: 'sdl',
    /**
     * Stock (spec/13 §6.4, ADR-0040; F-FNB-07 … F-FNB-13): a venue's stock item — an ingredient, a retail item or a
     * supply — counted in its base unit.
     */
    stockItem: 'ski',
    /** A pack unit of a stock item — "5 kg pack", "Case of 24" — how it is received and counted (ADR-0040 §2). */
    stockItemPack: 'skp',
    /** A place a venue keeps stock. One default per venue in v1; the id is on every movement (ADR-0040 §4). */
    stockLocation: 'skl',
    /** One change to what is on hand: sold, refunded, received, wasted, counted… Append-only (ADR-0040 §2). */
    stockMovement: 'skm',
    /** A delivery received into stock, with its supplier, lines and cost (F-FNB-09). */
    stockReceipt: 'skr',
    /** A full or spot count of a venue's stock, open while it is being counted (F-FNB-09). */
    stockCount: 'skc',
    /** Who a venue buys stock from (F-FNB-09). */
    stockSupplier: 'sup',
    /** What one unit of a product, a variant or a modifier takes from stock at one venue (F-FNB-07, ADR-0040 §3). */
    stockRecipe: 'rcp',
    /** One move of a café's players and balances from another platform, run by HQ (ADR-0048; F-HQ-40). */
    migrationRun: 'mig',
    /** A café HQ's sales team is trying to sign (F-HQ-39, spec/14 §16). */
    lead: 'lead',
    /** One entry of a lead's timeline: a message, a call, a stage change … (F-HQ-39). */
    leadActivity: 'lact',
    /** HQ's message draft for outreach, with placeholders (F-HQ-39). */
    leadTemplate: 'ltpl',
    /** A provider-priced add-on attached to an organisation — Clavian Boot for N PCs (F-BOOT-17, spec/14 §4.5). */
    organizationAddon: 'oadd',
    /**
     * A player record brought over from a café's previous software, waiting for its owner to claim it — never an
     * account: an account is made only with its owner's consent (ADR-0048, spec/07 §3.1).
     */
    importedPlayer: 'imp',
    /**
     * Clavian Boot (ADR-0063, spec/25 §14). A **boot server**: the one Windows machine in a venue that network-boots its
     * PCs, paired to that venue like a station and authenticated as device kind `boot_server`.
     */
    bootServer: 'bsv',
    /** A boot **image**: a bootable Windows system disk, or a shared game disk — a base VHDX plus its versions. */
    bootImage: 'bim',
    /** One **image version**: a differencing layer on the one before it, and a restore point once published. */
    bootImageVersion: 'biv',
    /** A **boot PC**: a PC that network-boots from the venue's boot server, known by its MAC; usually also a station. */
    bootPc: 'bpc',
    /** An **edit boot**: one PC booting an image with its writes going into a new, unpublished version. */
    bootEdit: 'bed',
    /** A shader-cache **force-wipe** request, for one PC, one GPU family or the fleet (spec/25 §11). */
    bootShaderWipe: 'bsw',
};
/**
 * The schema for a TypeID with one prefix. The parsed value is branded, so a `StationId` cannot be
 * passed where a `SessionId` was meant even though both are strings at runtime.
 *
 * ```ts
 * export const LeaseIdSchema = typeIdSchema('lse', 'LeaseId');
 * export type LeaseId = z.infer<typeof LeaseIdSchema>;
 * ```
 *
 * The branding is type-level only: no transform runs, so the schema stays a plain string to
 * OpenAPI, to JSON Schema and to every generated client.
 */
export function typeIdSchema(prefix, componentName) {
    const pattern = typeIdPattern(prefix);
    const schema = z
        .string()
        .regex(pattern, `Expected a TypeID beginning "${prefix}_"`)
        .openapi(componentName, {
        description: `A TypeID: "${prefix}_" followed by the base32 form of a UUIDv7.`,
        // A real encoded UUIDv7 (01997f5e-1234-7abc-8def-0123456789ab). The alphabet is Crockford's
        // without `i`, `l`, `o` and `u`, so an example has to be generated rather than typed: the
        // one this replaced contained a `u` and did not match the pattern it was an example of.
        example: `${prefix}_01k5znw4hmfay8vvr14d2pf2db`,
    });
    return schema;
}
export const OrganizationIdSchema = typeIdSchema(ID_PREFIXES.organization, 'OrganizationId');
export const VenueIdSchema = typeIdSchema(ID_PREFIXES.venue, 'VenueId');
export const StationIdSchema = typeIdSchema(ID_PREFIXES.station, 'StationId');
export const PlayerIdSchema = typeIdSchema(ID_PREFIXES.player, 'PlayerId');
export const SessionIdSchema = typeIdSchema(ID_PREFIXES.session, 'SessionId');
export const MessageIdSchema = typeIdSchema(ID_PREFIXES.message, 'MessageId');
export const RequestIdSchema = typeIdSchema(ID_PREFIXES.request, 'RequestId');
export const ScopeProbeIdSchema = typeIdSchema(ID_PREFIXES.scopeProbe, 'ScopeProbeId');
export const AuditEntryIdSchema = typeIdSchema(ID_PREFIXES.auditEntry, 'AuditEntryId');
export const FileIdSchema = typeIdSchema(ID_PREFIXES.file, 'FileId');
export const ZoneIdSchema = typeIdSchema(ID_PREFIXES.zone, 'ZoneId');
export const StationClassIdSchema = typeIdSchema(ID_PREFIXES.stationClass, 'StationClassId');
export const ConsentIdSchema = typeIdSchema(ID_PREFIXES.consent, 'ConsentId');
export const MemberIdSchema = typeIdSchema(ID_PREFIXES.member, 'MemberId');
export const InvitationIdSchema = typeIdSchema(ID_PREFIXES.invitation, 'InvitationId');
export const PlayerBanIdSchema = typeIdSchema(ID_PREFIXES.playerBan, 'PlayerBanId');
export const PlayerNoteIdSchema = typeIdSchema(ID_PREFIXES.playerNote, 'PlayerNoteId');
export const IdCheckIdSchema = typeIdSchema(ID_PREFIXES.idCheck, 'IdCheckId');
export const AuthSessionIdSchema = typeIdSchema(ID_PREFIXES.authSession, 'AuthSessionId');
export const StationCommandIdSchema = typeIdSchema(ID_PREFIXES.stationCommand, 'StationCommandId');
export const DeviceIdSchema = typeIdSchema(ID_PREFIXES.device, 'DeviceId');
export const StationDisplayIdSchema = typeIdSchema(ID_PREFIXES.stationDisplay, 'StationDisplayId');
export const StockItemIdSchema = typeIdSchema(ID_PREFIXES.stockItem, 'StockItemId');
export const StockItemPackIdSchema = typeIdSchema(ID_PREFIXES.stockItemPack, 'StockItemPackId');
export const StockLocationIdSchema = typeIdSchema(ID_PREFIXES.stockLocation, 'StockLocationId');
export const StockMovementIdSchema = typeIdSchema(ID_PREFIXES.stockMovement, 'StockMovementId');
export const StockReceiptIdSchema = typeIdSchema(ID_PREFIXES.stockReceipt, 'StockReceiptId');
export const StockCountIdSchema = typeIdSchema(ID_PREFIXES.stockCount, 'StockCountId');
export const StockSupplierIdSchema = typeIdSchema(ID_PREFIXES.stockSupplier, 'StockSupplierId');
export const StockRecipeIdSchema = typeIdSchema(ID_PREFIXES.stockRecipe, 'StockRecipeId');
export const DevicePairingIdSchema = typeIdSchema(ID_PREFIXES.devicePairing, 'DevicePairingId');
export const GameIdSchema = typeIdSchema(ID_PREFIXES.game, 'GameId');
export const GameTrailerIdSchema = typeIdSchema(ID_PREFIXES.gameTrailer, 'GameTrailerId');
export const LibraryEntryIdSchema = typeIdSchema(ID_PREFIXES.libraryEntry, 'LibraryEntryId');
export const SessionSegmentIdSchema = typeIdSchema(ID_PREFIXES.sessionSegment, 'SessionSegmentId');
export const SessionEventIdSchema = typeIdSchema(ID_PREFIXES.sessionEvent, 'SessionEventId');
export const LedgerAccountIdSchema = typeIdSchema(ID_PREFIXES.ledgerAccount, 'LedgerAccountId');
export const JournalEntryIdSchema = typeIdSchema(ID_PREFIXES.journalEntry, 'JournalEntryId');
export const LedgerLineIdSchema = typeIdSchema(ID_PREFIXES.ledgerLine, 'LedgerLineId');
export const MenuCategoryIdSchema = typeIdSchema(ID_PREFIXES.menuCategory, 'MenuCategoryId');
export const ProductIdSchema = typeIdSchema(ID_PREFIXES.product, 'ProductId');
export const ProductVariantIdSchema = typeIdSchema(ID_PREFIXES.productVariant, 'ProductVariantId');
export const ProductModifierGroupIdSchema = typeIdSchema(ID_PREFIXES.productModifierGroup, 'ProductModifierGroupId');
export const ProductModifierIdSchema = typeIdSchema(ID_PREFIXES.productModifier, 'ProductModifierId');
export const FoodOrderIdSchema = typeIdSchema(ID_PREFIXES.foodOrder, 'FoodOrderId');
export const FoodOrderLineIdSchema = typeIdSchema(ID_PREFIXES.foodOrderLine, 'FoodOrderLineId');
export const FoodOrderLineModifierIdSchema = typeIdSchema(ID_PREFIXES.foodOrderLineModifier, 'FoodOrderLineModifierId');
export const KitchenTicketIdSchema = typeIdSchema(ID_PREFIXES.kitchenTicket, 'KitchenTicketId');
export const KitchenTicketItemIdSchema = typeIdSchema(ID_PREFIXES.kitchenTicketItem, 'KitchenTicketItemId');
export const PassProductIdSchema = typeIdSchema(ID_PREFIXES.passProduct, 'PassProductId');
export const PassProductVersionIdSchema = typeIdSchema(ID_PREFIXES.passProductVersion, 'PassProductVersionId');
export const PassHoldingIdSchema = typeIdSchema(ID_PREFIXES.passHolding, 'PassHoldingId');
export const GuestIdSchema = typeIdSchema(ID_PREFIXES.guest, 'GuestId');
export const PassPurchaseIdSchema = typeIdSchema(ID_PREFIXES.passPurchase, 'PassPurchaseId');
export const OfflineStaffCodeIdSchema = typeIdSchema(ID_PREFIXES.offlineStaffCode, 'OfflineStaffCodeId');
export const OfflineSessionIdSchema = typeIdSchema(ID_PREFIXES.offlineSession, 'OfflineSessionId');
export const WalletIdSchema = typeIdSchema(ID_PREFIXES.wallet, 'WalletId');
export const WalletTransactionIdSchema = typeIdSchema(ID_PREFIXES.walletTransaction, 'WalletTransactionId');
export const WalletTopUpRequestIdSchema = typeIdSchema(ID_PREFIXES.walletTopUpRequest, 'WalletTopUpRequestId');
export const SaleIdSchema = typeIdSchema(ID_PREFIXES.sale, 'SaleId');
export const SaleLineIdSchema = typeIdSchema(ID_PREFIXES.saleLine, 'SaleLineId');
export const SalePaymentIdSchema = typeIdSchema(ID_PREFIXES.salePayment, 'SalePaymentId');
export const PaymentIntentIdSchema = typeIdSchema(ID_PREFIXES.paymentIntent, 'PaymentIntentId');
export const CustomAppIdSchema = typeIdSchema(ID_PREFIXES.customApp, 'CustomAppId');
export const CatalogueRequestIdSchema = typeIdSchema(ID_PREFIXES.catalogueRequest, 'CatalogueRequestId');
export const GamePlayIdSchema = typeIdSchema(ID_PREFIXES.gamePlay, 'GamePlayId');
export const StationAlertIdSchema = typeIdSchema(ID_PREFIXES.stationAlert, 'StationAlertId');
export const SaleRefundIdSchema = typeIdSchema(ID_PREFIXES.saleRefund, 'SaleRefundId');
export const SaleDocumentIdSchema = typeIdSchema(ID_PREFIXES.saleDocument, 'SaleDocumentId');
export const DiscountCodeIdSchema = typeIdSchema(ID_PREFIXES.discountCode, 'DiscountCodeId');
export const ShiftIdSchema = typeIdSchema(ID_PREFIXES.shift, 'ShiftId');
export const CashMovementIdSchema = typeIdSchema(ID_PREFIXES.cashMovement, 'CashMovementId');
export const CounterIdSchema = typeIdSchema(ID_PREFIXES.counter, 'CounterId');
export const BusinessDayCloseIdSchema = typeIdSchema(ID_PREFIXES.businessDayClose, 'BusinessDayCloseId');
export const VenueTaxRegistrationIdSchema = typeIdSchema(ID_PREFIXES.venueTaxRegistration, 'VenueTaxRegistrationId');
export const SessionCallIdSchema = typeIdSchema(ID_PREFIXES.sessionCall, 'SessionCallId');
export const ReservationIdSchema = typeIdSchema(ID_PREFIXES.reservation, 'ReservationId');
export const NotificationIdSchema = typeIdSchema(ID_PREFIXES.notification, 'NotificationId');
export const MessageDeliveryIdSchema = typeIdSchema(ID_PREFIXES.messageDelivery, 'MessageDeliveryId');
export const NotificationPreferenceIdSchema = typeIdSchema(ID_PREFIXES.notificationPreference, 'NotificationPreferenceId');
export const PromotionIdSchema = typeIdSchema(ID_PREFIXES.promotion, 'PromotionId');
export const CouponBatchIdSchema = typeIdSchema(ID_PREFIXES.couponBatch, 'CouponBatchId');
export const CouponIdSchema = typeIdSchema(ID_PREFIXES.coupon, 'CouponId');
export const CouponRedemptionIdSchema = typeIdSchema(ID_PREFIXES.couponRedemption, 'CouponRedemptionId');
export const CreditAccountIdSchema = typeIdSchema(ID_PREFIXES.creditAccount, 'CreditAccountId');
export const TaxCategoryIdSchema = typeIdSchema(ID_PREFIXES.taxCategory, 'TaxCategoryId');
export const CreditNoteIdSchema = typeIdSchema(ID_PREFIXES.creditNote, 'CreditNoteId');
export const PrinterIdSchema = typeIdSchema(ID_PREFIXES.printer, 'PrinterId');
export const PrintJobIdSchema = typeIdSchema(ID_PREFIXES.printJob, 'PrintJobId');
export const RateCardVersionIdSchema = typeIdSchema(ID_PREFIXES.rateCardVersion, 'RateCardVersionId');
export const MigrationRunIdSchema = typeIdSchema(ID_PREFIXES.migrationRun, 'MigrationRunId');
export const LeadIdSchema = typeIdSchema(ID_PREFIXES.lead, 'LeadId');
export const LeadActivityIdSchema = typeIdSchema(ID_PREFIXES.leadActivity, 'LeadActivityId');
export const LeadTemplateIdSchema = typeIdSchema(ID_PREFIXES.leadTemplate, 'LeadTemplateId');
export const OrganizationAddonIdSchema = typeIdSchema(ID_PREFIXES.organizationAddon, 'OrganizationAddonId');
export const ImportedPlayerIdSchema = typeIdSchema(ID_PREFIXES.importedPlayer, 'ImportedPlayerId');
export const BootServerIdSchema = typeIdSchema(ID_PREFIXES.bootServer, 'BootServerId');
export const BootImageIdSchema = typeIdSchema(ID_PREFIXES.bootImage, 'BootImageId');
export const BootImageVersionIdSchema = typeIdSchema(ID_PREFIXES.bootImageVersion, 'BootImageVersionId');
export const BootPcIdSchema = typeIdSchema(ID_PREFIXES.bootPc, 'BootPcId');
export const BootEditIdSchema = typeIdSchema(ID_PREFIXES.bootEdit, 'BootEditId');
export const BootShaderWipeIdSchema = typeIdSchema(ID_PREFIXES.bootShaderWipe, 'BootShaderWipeId');
//# sourceMappingURL=schema.js.map